import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Merge Tailwind CSS classes with proper precedence
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format phone number for display (e.g., +1234567890 -> +1 234 567 890)
 */
export function formatPhoneNumber(phone: string): string {
  if (!phone) return ''

  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '')

  // Format: +X XXX XXX XXX
  if (cleaned.startsWith('+')) {
    const countryCode = cleaned.substring(0, cleaned.length >= 12 ? 3 : 2)
    const rest = cleaned.substring(countryCode.length)
    const parts = rest.match(/.{1,3}/g) || []
    return `${countryCode} ${parts.join(' ')}`
  }

  return cleaned
}

/**
 * Validate phone number (basic check for E.164 format)
 */
export function isValidPhoneNumber(phone: string): boolean {
  // E.164 format: +[country code][subscriber number]
  // Length: 7-15 digits (excluding +)
  const pattern = /^\+[1-9]\d{6,14}$/
  return pattern.test(phone)
}

/**
 * Format currency amount
 */
export function formatCurrency(amount: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
  }).format(amount / 100) // Stripe amounts are in cents
}

/**
 * Format date
 */
export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(date))
}

/**
 * Sleep utility for delays
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Format commitment period (1, 6, or 12 months) to display string
 */
export function formatCommitmentPeriod(months: number): string {
  if (months === 1) return '1 Month'
  if (months === 6) return '6 Months'
  if (months === 12) return '12 Months'
  return `${months} Months`
}
